import type { IFacilitatorSuggestion, TCoachCategory, TCoachTone } from "@lexicanon/shared"
import type { MessageDescriptor } from "@lingui/core"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Button, SegmentedControl, Stack, Switch, Text } from "@mantine/core"
import { IconBulb, IconSparkles } from "@tabler/icons-react"
import classes from "./CoachCard.module.css"

/** Category → semantic tone (never a literal hue). Mirrors the DM-coaching map. */
type TTone = "info" | "brand" | "highlight" | "danger" | "warning"

const CATEGORY_TONES: Record<TCoachCategory, TTone> = {
  clarify: "info",
  decide: "brand",
  challenge: "highlight",
  risk: "danger",
  refocus: "warning",
}

const CATEGORY_LABEL: Record<TCoachCategory, MessageDescriptor> = {
  clarify: msg`Clarify`,
  decide: msg`Decide`,
  challenge: msg`Challenge`,
  risk: msg`Risk`,
  refocus: msg`Refocus`,
}

interface IProps {
  /** Whether the coach is running for this recording. */
  enabled: boolean
  /** How pointed the questions are. */
  tone: TCoachTone
  /** The current suggestion, or null while waiting. */
  suggestion: IFacilitatorSuggestion | null
  /** Recent suggestions (newest first) — shown small under the current one. */
  history: IFacilitatorSuggestion[]
  /** True while a manual "Suggest a question" pull is in flight. */
  asking?: boolean
  /** Show the Gentle / Balanced / Challenger control. Default true; lite hides it. */
  showTone?: boolean
  onToggle: (enabled: boolean) => void
  onToneChange: (tone: TCoachTone) => void
  onAsk: () => void
}

/**
 * CoachCard — the "Worth asking" facilitator coach surface. Shows the latest
 * steering question (with a category badge) plus an on/off switch, a live tone
 * control (Gentle / Balanced / Challenger), and a manual "Suggest a question"
 * pull. Pure + dumb; the smart wrapper owns the WS config + suggestion feed.
 */
export function CoachCard({
  enabled,
  tone,
  suggestion,
  history,
  asking = false,
  showTone = true,
  onToggle,
  onToneChange,
  onAsk,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  const toneData = [
    { label: i18n._(msg`Gentle`), value: "gentle" },
    { label: i18n._(msg`Balanced`), value: "balanced" },
    { label: i18n._(msg`Challenger`), value: "challenger" },
  ]

  const recent = history.filter((h) => h.timestamp !== suggestion?.timestamp).slice(0, 3)

  return (
    <div
      className={`${classes.card} lex-surface-card`}
      data-testid="coach-card"
      data-enabled={enabled ? "true" : "false"}
    >
      <div className={classes.head}>
        <span className={classes.overline}>
          <IconSparkles size={12} />
          <Trans>Worth asking</Trans>
        </span>
        <Switch
          size="sm"
          checked={enabled}
          onChange={(e) => onToggle(e.currentTarget.checked)}
          aria-label={i18n._(msg`Toggle facilitator coach`)}
        />
      </div>

      {enabled ? (
        <>
          <div className={classes.controls}>
            {showTone ? (
              <SegmentedControl
                size="xs"
                value={tone}
                onChange={(v) => onToneChange(v as TCoachTone)}
                data={toneData}
                aria-label={i18n._(msg`Coach tone`)}
              />
            ) : null}
            <Button
              size="compact-xs"
              variant="light"
              leftSection={<IconBulb size={13} />}
              loading={asking}
              onClick={onAsk}
            >
              <Trans>Suggest a question</Trans>
            </Button>
          </div>

          {suggestion ? (
            <div
              key={suggestion.timestamp}
              className={classes.questionCard}
              data-tone={CATEGORY_TONES[suggestion.category]}
            >
              <Badge size="xs" color={CATEGORY_TONES[suggestion.category]} mb={6}>
                {i18n._(CATEGORY_LABEL[suggestion.category])}
              </Badge>
              <Text className={classes.question}>{suggestion.question}</Text>
              {suggestion.why && <Text className={classes.why}>{suggestion.why}</Text>}
            </div>
          ) : (
            <Text className={classes.empty}>
              <Trans>Listening for a good moment to chime in…</Trans>
            </Text>
          )}

          {recent.length > 0 && (
            <Stack gap={4} className={classes.history}>
              {recent.map((h) => (
                <div key={h.timestamp} className={classes.historyItem} data-tone={CATEGORY_TONES[h.category]}>
                  <Badge size="xs" variant="light" color={CATEGORY_TONES[h.category]}>
                    {i18n._(CATEGORY_LABEL[h.category])}
                  </Badge>
                  <Text className={classes.historyText}>{h.question}</Text>
                </div>
              ))}
            </Stack>
          )}
        </>
      ) : (
        <Text className={classes.offHint}>
          <Trans>Coach is off. Turn it on for the occasional steering question during the meeting.</Trans>
        </Text>
      )}
    </div>
  )
}
